import { Search, X } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { debounce } from '@/lib/debounce'
import { normalizeSearch as normalize } from '@/lib/normalizeSearch'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { Input } from './Input'

type SearchInputProps = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  delay?: number
}

/**
 * Local state for instant typing; the debounced value drives the request.
 * Only a change in meaning sends one: typing spaces (leading, trailing or
 * repeated) never does, a box of only spaces counts as empty, and clearing
 * sends once — only if a search was actually applied.
 */
export function SearchInput({ value, onChange, placeholder, className, delay = 300 }: SearchInputProps) {
  const t = useT()
  const [draft, setDraft] = useState(value)
  const [synced, setSynced] = useState(value)
  const applied = useRef(value)
  useLayoutEffect(() => {
    applied.current = value
  })
  const send = useCallback(
    (raw: string) => {
      const next = normalize(raw)
      if (next !== normalize(applied.current)) onChange(next)
    },
    [onChange],
  )
  const emit = useMemo(() => debounce(send, delay), [send, delay])

  // Follow external changes (e.g. "Clear filters", back navigation) — but not the
  // echo of our own normalized search, which would eat a space typed between words.
  if (value !== synced) {
    setSynced(value)
    if (normalize(draft) !== normalize(value)) setDraft(value)
  }

  useEffect(() => () => emit.cancel(), [emit])

  return (
    <div className={cn('w-full sm:w-72', className)}>
      <Input
        type="search"
        value={draft}
        placeholder={placeholder ?? t('common.search')}
        aria-label={placeholder ?? t('common.search')}
        onChange={(event) => {
          setDraft(event.target.value)
          emit(event.target.value)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            emit.cancel()
            if (normalize(draft) !== normalize(value)) onChange(normalize(draft))
          }
        }}
        leading={<Search className="size-3.75" />}
        trailing={
          draft ? (
            <button
              type="button"
              aria-label={t('common.clearSearch')}
              onClick={() => {
                setDraft('')
                emit.cancel()
                if (normalize(value)) onChange('')
              }}
              className="flex size-6 items-center justify-center rounded-[calc(6px*var(--radius-scale))] text-subtle hover:bg-hover hover:text-fg"
            >
              <X className="size-3.5" />
            </button>
          ) : undefined
        }
        className="[&::-webkit-search-cancel-button]:hidden"
      />
    </div>
  )
}
